import React, { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { SuggestionStatus, SuggestionType } from '@dazzle/api-client';

import { NotificationOutlet, useNotificationContext } from 'Common/contexts/NotificationContext';
import { BannerLevel } from 'Common/components/Banner';
import BlueSpinner from 'Common/components/BlueSpinner';
import SubmittingOverlay from 'Common/components/SubmittingOverlay';
import { DeviceQueries } from 'Common/components/styled';
import { suggestionUpdateInternal } from 'Common/api/DazzleInternalService';
import { logSumoEvent, ULogApplication, ULogSeverity, ULogTag } from 'Common/api/SumoLogic';
import { toSuggestion } from 'Common/util/Suggestion';
import { sanitizeUrl } from 'Common/util/Links';

import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import { Page } from 'Compass/core/MainLayout';
import { IUFullSuggestion, useSuggestionsContext } from 'Compass/contexts/SuggestionsContext';
import { Button, ButtonTheme } from 'Compass/components/styled/Button';
import { formatDate } from 'Compass/utils/orderUtils';
import { useUserContext } from 'Compass/contexts/UserContext';

const OrderSuggestionPage = () => {
  const { suggestionId } = useParams();
  const navigate = useNavigate();
  const suggestionsContext = useSuggestionsContext();
  const notificationContext = useNotificationContext();
  const userContext = useUserContext();

  const [fullSuggestion, setFullSuggestion] = useState<IUFullSuggestion | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const suggestion = fullSuggestion?.suggestion;
  const suggestionContent = suggestion?.content;

  useEffect(() => {
    const fetchSuggestion = async () => {
      if (suggestionId) {
        try {
          const suggestion = await suggestionsContext.getSuggestion(suggestionId);

          setFullSuggestion(suggestion);
        } catch (e) {
          void logSumoEvent({
            app: ULogApplication.COMPASS,
            severity: ULogSeverity.ERROR,
            tag: ULogTag.UserAction,
            userId: userContext.name,
            message: `[OrderSuggestionPage] Error fetching suggestion (${suggestionId}): ${e.message ?? e}`,
          });

          notificationContext.showBanner({
            level: BannerLevel.ERROR,
            contents: `An error occurred while fetching the suggestion: ${e.message ?? e}`,
          });
        } finally {
          setLoading(false);
        }
      }
    };

    void fetchSuggestion();
  }, [notificationContext, suggestionId, suggestionsContext, userContext.name]);

  const onReject = async () => {
    if (!suggestion) {
      return;
    }

    void logSumoEvent({
      app: ULogApplication.COMPASS,
      severity: ULogSeverity.INFO,
      tag: ULogTag.UserAction,
      userId: userContext.name,
      message: `[OrderSuggestionPage] Rejecting suggestion ${fullSuggestion.suggestion.suggestionId}`
    });

    setSubmitting(true);
    try {
      await suggestionUpdateInternal(toSuggestion({
        ...fullSuggestion.suggestion,
        status: SuggestionStatus.Invalid
      }));

      notificationContext.showBanner({
        level: BannerLevel.INFO,
        contents: 'Suggestion rejected',
        duration: 5000
      });
      navigate('/orders');
    } catch (e) {
      notificationContext.showBanner({
        level: BannerLevel.ERROR,
        contents: `Something went wrong: ${e.message ?? e}`
      });
    }
    setSubmitting(false);
  };

  const onApprove = async () => {
    if (!suggestion) {
      return;
    }

    void logSumoEvent({
      app: ULogApplication.COMPASS,
      severity: ULogSeverity.INFO,
      tag: ULogTag.UserAction,
      userId: userContext.name,
      message: `[OrderSuggestionPage] Approving suggestion ${fullSuggestion.suggestion.suggestionId}`
    });

    setSubmitting(true);
    try {
      await suggestionUpdateInternal(toSuggestion({
        ...fullSuggestion.suggestion,
        status: SuggestionStatus.Pending
      }));

      notificationContext.showBanner({
        level: BannerLevel.INFO,
        contents: 'Suggestion approved',
        duration: 5000
      });

      navigate('/orders');
    } catch (e) {
      notificationContext.showBanner({
        level: BannerLevel.ERROR,
        contents: `Something went wrong: ${e.message ?? e}`
      });
    }
    setSubmitting(false);
  };

  const getStatusColor = (status: SuggestionStatus): string => {
    switch (status) {
      case SuggestionStatus.PendingReview:
        return '#008AFF';
      case SuggestionStatus.Pending:
        return '#10b981';
      case SuggestionStatus.Seen:
        return '#6366f1';
      case SuggestionStatus.Dismissed:
        return '#6b7280';
      case SuggestionStatus.ActedOn:
        return '#059669';
      case SuggestionStatus.Invalid:
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getQualityFeedbackColor = (feedback: string | undefined): string => {
    switch (feedback) {
      case 'GOOD':
        return '#48BB78';
      case 'NEUTRAL':
        return '#ECC94B';
      case 'BAD':
        return '#F56565';
      default:
        return '#8890A5';
    }
  };

  const isPendingReview = suggestion?.status === SuggestionStatus.PendingReview;

  let content: ReactNode;
  if (loading) {
    content = <CenteredContainer><BlueSpinner/></CenteredContainer>;
  } else if (!fullSuggestion || !suggestion) {
    content = <NotificationOutlet/>;
  } else {
    content = (
      <>
        <NotificationOutlet />
        <Card>
          <CardHeader>
            <OrderTitle>
              {suggestionContent?.type === SuggestionType.Product ? 'Purchase Suggestion' : 'Suggestion Details'}
            </OrderTitle>
            <BadgeGroup>
              <StatusBadge $color={getStatusColor(suggestion.status ?? SuggestionStatus.Pending)}>
                {(suggestion.status ?? 'Unknown').replace(/_/g, ' ')}
              </StatusBadge>
              {suggestion.qualityFeedback && suggestion.qualityFeedback !== 'NONE' && (
                <QualityBadge $color={getQualityFeedbackColor(suggestion.qualityFeedback)}>
                  Feedback: {suggestion.qualityFeedback}
                </QualityBadge>
              )}
            </BadgeGroup>
          </CardHeader>

          <TwoColumn>
            <Section>
              <SectionTitle>
                {suggestionContent?.type === SuggestionType.Product ? 'Product Information' : 'Suggestion Information'}
              </SectionTitle>
              <DataTable>
                <Row>
                  <Label>Title</Label>
                  <Value>{suggestion.title || 'N/A'}</Value>
                </Row>
                <Row>
                  <Label>Type</Label>
                  <Value><TypeBadge>{suggestion.suggestionType}</TypeBadge></Value>
                </Row>
              </DataTable>
            </Section>
            {fullSuggestion?.photoUrl && <ProductImage src={fullSuggestion.photoUrl} />}
          </TwoColumn>

          <Divider />

          <Section>
            <SectionTitle>User Details</SectionTitle>
            <DataTable>
              <Row>
                <Label>User Name</Label>
                <Value>{fullSuggestion.userName || 'N/A'}</Value>
              </Row>
              <Row>
                <Label>Phone Number</Label>
                <Value>{fullSuggestion.userPhoneNumber || 'N/A'}</Value>
              </Row>
            </DataTable>
          </Section>

          <Divider />
          <Section>
            <SectionTitle>Suggestion Details</SectionTitle>
            <DataTable>
              {suggestionContent && (
                <Row>
                  <Label>Message from Dazzle</Label>
                  <Value>{suggestionContent.message}</Value>
                </Row>
              )}
              {fullSuggestion.address && (
                <Row>
                  <Label>Address</Label>
                  <Value>{fullSuggestion.address}</Value>
                </Row>
              )}
              {suggestionContent?.type === SuggestionType.Product && suggestionContent.event && (
                <>
                  <Row>
                    <Label>Estimated Price</Label>
                    <Value>{suggestionContent.event.price}</Value>
                  </Row>
                  <Row>
                    <Label>URLs</Label>
                    <Value>
                      <Urls>
                        {suggestionContent.event.urls?.map((url, i) => (
                          <a href={sanitizeUrl(url)} target="_blank" key={i}>
                            {url}
                          </a>
                        ))}
                      </Urls>
                    </Value>
                  </Row>
                </>
              )}
              <Row>
                <Label>Last Updated</Label>
                <Value>{formatDate(fullSuggestion.suggestion.lastUpdatedTs)}</Value>
              </Row>
              {suggestion.qualityFeedback && (
                <Row>
                  <Label>Quality Feedback</Label>
                  <Value>
                    <QualityFeedbackBadge $color={getQualityFeedbackColor(suggestion.qualityFeedback)}>
                      {suggestion.qualityFeedback}
                    </QualityFeedbackBadge>
                  </Value>
                </Row>
              )}
            </DataTable>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>Actions</SectionTitle>
            {isPendingReview ? (
              <ButtonGroup>
                <Button $theme={ButtonTheme.secondary} onClick={onReject}>
                  Reject
                </Button>
                <Button onClick={onApprove}>Approve</Button>
              </ButtonGroup>
            ) : (
              <ButtonGroup>
                <ReadOnlyMessage>
                  This suggestion is in &quot;{(suggestion.status ?? 'Unknown').replace(/_/g, ' ')}&quot; status and cannot be modified.
                </ReadOnlyMessage>
                <Button $theme={ButtonTheme.secondary} onClick={() => navigate(-1)}>
                  Go Back
                </Button>
              </ButtonGroup>
            )}
          </Section>
        </Card>
      </>
    );
  }

  return <>
    <CompassHeader pageType={PageType.Orders}/>
    <Page>
      <SubmittingOverlay show={submitting}/>
      <PageContainer>
        {content}
      </PageContainer>
    </Page>
  </>;
};

const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 0 auto;
  position: relative;

  @media (${DeviceQueries.mobile}) {
    padding: 0;
  }
`;

const Card = styled.div`
  background: white;
  overflow: hidden;

  @media screen and (${DeviceQueries.notMobile}) {
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    border: 1px solid #E4E7EF;
  }
`;

const CardHeader = styled.div`
  padding: 24px 32px;
  background: linear-gradient(to bottom, #FAFAFC, #ffffff);
  border-bottom: 1px solid #E4E7EF;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: center;

  @media (${DeviceQueries.mobile}) {
    flex-direction: column;
  }
`;

const OrderTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1A1D2B;
  margin: 0;
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatusBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background-color: ${props => props.$color};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QualityBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$color};
  background-color: ${props => props.$color}20;
  border: 2px solid ${props => props.$color};
  letter-spacing: 0.5px;
`;

const QualityFeedbackBadge = styled.span<{ $color: string }>`
  padding: 6px 14px;
  border-radius: 6px;
  background: ${props => props.$color}20;
  color: ${props => props.$color};
  font-size: 14px;
  font-weight: 600;
`;

const TypeBadge = styled.span`
  padding: 4px 10px;
  border-radius: 4px;
  background: #E4E7EF;
  font-size: 13px;
  font-weight: 500;
  color: #5A6178;
`;

const ReadOnlyMessage = styled.p`
  font-size: 14px;
  color: #5A6178;
  margin: 0;
  padding: 12px 16px;
  background: #F8F9FC;
  border-radius: 8px;
  border-left: 4px solid #8890A5;
`;

const TwoColumn = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;

  @media (${DeviceQueries.mobile}) {
    flex-direction: column-reverse;
  }
`;

const Section = styled.div`
  padding: 24px 32px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #5A6178;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DataTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #F0F1F6;

  &:last-child {
    border-bottom: none;
  }

  @media (${DeviceQueries.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #5A6178;
  display: flex;
  align-items: center;
`;

const Value = styled.div`
  font-size: 14px;
  color: #1A1D2B;
  display: flex;
  align-items: center;
  word-break: break-word;
`;

const ProductImage = styled.img`
  margin: auto;
  height: 400px;
  width: auto;
  cursor: pointer;
`;

const Divider = styled.div`
  height: 1px;
  background: #E4E7EF;
  margin: 0 32px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;

  @media (${DeviceQueries.mobile}) {
    flex-direction: column;
  }
`;

const CenteredContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: space-around;
`;

const Urls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export default OrderSuggestionPage;
